import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Componente per proteggere le route in base ai permessi
 * 
 * Usage:
 * <Route path="/scraping" element={
 *   <ProtectedRoute requiredPermission="view_scraping">
 *     <ScrapingPage />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute = ({ 
  children, 
  requiredPermission = null,
  requiredRole = null,
  requiredAnyPermission = null,
  fallbackPath = '/dashboard'
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasPermission, hasRole, hasAnyPermission } = usePermissions();

  // Mostra loading mentre verifica l'autenticazione
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Se non autenticato, redirect al login
  if (!isAuthenticated) {
    console.warn('🚫 ProtectedRoute: Utente non autenticato, redirect a /login');
    return <Navigate to="/login" replace />;
  }

  // Verifica permesso specifico
  if (requiredPermission && !hasPermission(requiredPermission)) {
    console.warn(`🚫 ProtectedRoute: Permesso '${requiredPermission}' richiesto ma non disponibile, redirect a ${fallbackPath}`);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accesso Negato</h2>
          <p className="text-gray-600 mb-6">
            Non hai i permessi necessari per accedere a questa pagina.
          </p>
          <button
            onClick={() => window.location.href = fallbackPath}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Verifica ruolo specifico
  if (requiredRole && !hasRole(requiredRole)) {
    console.warn(`🚫 ProtectedRoute: Ruolo '${requiredRole}' richiesto ma non disponibile, redirect a ${fallbackPath}`);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accesso Negato</h2>
          <p className="text-gray-600 mb-6">
            Questa pagina è riservata agli utenti con ruolo <strong>{requiredRole}</strong>.
          </p>
          <button
            onClick={() => window.location.href = fallbackPath}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Verifica almeno uno dei permessi
  if (requiredAnyPermission && !hasAnyPermission(requiredAnyPermission)) {
    console.warn(`🚫 ProtectedRoute: Almeno uno dei permessi ${requiredAnyPermission.join(', ')} richiesto ma non disponibile, redirect a ${fallbackPath}`);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accesso Negato</h2>
          <p className="text-gray-600 mb-6">
            Non hai i permessi necessari per accedere a questa pagina.
          </p>
          <button
            onClick={() => window.location.href = fallbackPath}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Se tutti i controlli passano, mostra il contenuto
  console.log('✅ ProtectedRoute: Accesso consentito');
  return children;
};

export default ProtectedRoute;

